import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  initPwaInstallCapture,
  getDeferredInstallPrompt,
  subscribeDeferredInstallPrompt,
  clearDeferredInstallPrompt,
  isPwaStandaloneMode,
  isMobileLikeDevice,
  isIosLikeDevice,
  isAndroidLikeDevice,
  getInstallPlatform,
} from '../utils/pwaInstallCapture';

const DISMISS_UNTIL_KEY = 'maveshi-pwa-install-dismissed-until-v2';

function isLocalhostHost() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return (
    host === 'localhost'
    || host === '[::1]'
    || /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(host)
  );
}

function readDismissed() {
  try {
    const until = localStorage.getItem(DISMISS_UNTIL_KEY);
    if (!until) return false;
    return Date.now() < Number(until);
  } catch {
    return false;
  }
}

initPwaInstallCapture();

export { isPwaStandaloneMode as isPwaStandalone };

export default function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(() => getDeferredInstallPrompt());
  const [isInstalled, setIsInstalled] = useState(() => isPwaStandaloneMode());
  const [dismissed, setDismissed] = useState(readDismissed);
  const [helpOpen, setHelpOpen] = useState(false);
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);

  useEffect(() => {
    return subscribeDeferredInstallPrompt((event) => {
      setDeferredPrompt(event);
    });
  }, []);

  useEffect(() => {
    if (!isPwaStandaloneMode()) return;
    setIsInstalled(true);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const syncSwState = () => {
      setServiceWorkerReady(Boolean(navigator.serviceWorker.controller));
    };

    syncSwState();
    navigator.serviceWorker.ready.then(() => setServiceWorkerReady(true)).catch(() => {});
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg?.active || reg?.installing || reg?.waiting) {
        setServiceWorkerReady(true);
      }
    }).catch(() => {});

    navigator.serviceWorker.addEventListener('controllerchange', syncSwState);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', syncSwState);
  }, []);

  const isProductionPwa =
    process.env.NODE_ENV === 'production'
    || process.env.REACT_APP_ENABLE_PWA === 'true';

  const pwaHostEnabled =
    isProductionPwa
    || (process.env.NODE_ENV === 'development' && isLocalhostHost());

  const isSecure = typeof window !== 'undefined' && window.isSecureContext === true;

  const installPlatform = useMemo(() => getInstallPlatform(), []);

  const canNativeInstall = Boolean(deferredPrompt) && !isInstalled;

  const manualInstallMode = useMemo(() => {
    if (canNativeInstall) return 'native';
    if (isIosLikeDevice()) return 'ios';
    if (isAndroidLikeDevice() || (isMobileLikeDevice() && installPlatform !== 'desktop')) {
      return 'android';
    }
    return 'desktop';
  }, [canNativeInstall, installPlatform]);

  const isInstallAvailable = Boolean(
    pwaHostEnabled
    && isSecure
    && !isInstalled
    && !isPwaStandaloneMode()
    && (
      canNativeInstall
      || isIosLikeDevice()
      || isMobileLikeDevice()
      || serviceWorkerReady
    )
  );

  const showInstallFab = isInstallAvailable && !dismissed;
  const showInstallPromo = false;

  const promptNativeInstall = useCallback(async () => {
    const promptEvent = deferredPrompt || getDeferredInstallPrompt();
    if (!promptEvent) {
      return { outcome: 'unavailable' };
    }
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      clearDeferredInstallPrompt();
      setDeferredPrompt(null);
      if (choice.outcome === 'accepted') {
        setIsInstalled(true);
      }
      return { outcome: choice.outcome };
    } catch (err) {
      console.warn('PWA install prompt failed:', err);
      return { outcome: 'error' };
    }
  }, [deferredPrompt]);

  const helpModalMode = useMemo(() => {
    if (manualInstallMode !== 'native') return manualInstallMode;
    if (installPlatform === 'ios') return 'ios';
    if (installPlatform === 'android') return 'android';
    return 'desktop';
  }, [manualInstallMode, installPlatform]);

  const installApp = useCallback(async () => {
    const promptEvent = deferredPrompt || getDeferredInstallPrompt();
    if (promptEvent && !isInstalled) {
      const result = await promptNativeInstall();
      if (result.outcome === 'accepted') {
        setHelpOpen(false);
        return result;
      }
    }
    setHelpOpen(true);
    return { outcome: 'manual' };
  }, [deferredPrompt, isInstalled, promptNativeInstall]);

  const dismissPromo = useCallback((days = 7) => {
    try {
      localStorage.setItem(DISMISS_UNTIL_KEY, String(Date.now() + days * 86400000));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }, []);

  return {
    isInstalled,
    showInstallPromo,
    showInstallFab,
    isInstallAvailable,
    canNativeInstall,
    manualInstallMode,
    helpModalMode,
    installPlatform,
    serviceWorkerReady,
    helpOpen,
    setHelpOpen,
    installApp,
    promptNativeInstall,
    dismissPromo,
    // legacy names for Layout
    canIosManualInstall: manualInstallMode === 'ios',
    canAndroidManualHint: manualInstallMode === 'android',
    canDesktopManualHint: manualInstallMode === 'desktop',
    iosHelpOpen: helpOpen,
    setIosHelpOpen: setHelpOpen,
    openIosInstallHelp: () => setHelpOpen(true),
    promptInstall: promptNativeInstall,
  };
};
